import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MapPin, Phone, Mail } from "lucide-react";
import { CONTACT } from "../lib/site";

const NAV = [
  { to: "/", label: "Haveli" },
  { to: "/about", label: "About" },
  { to: "/heritage-hotel", label: "Hotel & Booking" },
  { to: "/gallery", label: "Gallery" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-bg/85 backdrop-blur-xl border-b border-[#E5E1D8] py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 flex items-center justify-between gap-8">
        <Link to="/" className="group flex items-center gap-3" data-testid="brand-link">
          <span className="w-8 h-8 rounded-full border border-ink/40 flex items-center justify-center font-display text-[13px] text-ink group-hover:bg-terracotta group-hover:text-bg group-hover:border-terracotta transition-colors duration-500">
            H
          </span>
          <div className="leading-tight">
            <div className="font-display text-[15px] md:text-[16px] text-ink leading-tight">Haveli Braj Bhushanjee</div>
            <div className="overline text-terracotta text-[9px] mt-0.5">A Heritage Hotel &amp; Museum</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n, i) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-terracotta" : "text-ink hover:text-terracotta"
                }`
              }
              end={n.to === "/"}
            >
              <span className="overline">{String(i + 1).padStart(2, "0")}</span>
              <span className="ml-2">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${CONTACT.phone.replace(/\s+/g,'')}`}
            className="overline text-ink-soft hover:text-terracotta transition-colors"
            data-testid="nav-phone"
          >
            {CONTACT.phone} · {CONTACT.phone2}
          </a>
          <Link to="/heritage-hotel#booking" className="btn-pill btn-primary" data-testid="nav-book-btn">
            Reserve <span aria-hidden>→</span>
          </Link>
        </div>

        <button
          className="md:hidden p-2 rounded-full border border-ink/20"
          onClick={() => setOpen(v => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={18}/> : <Menu size={18}/>}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.2,0.7,0.2,1] }}
            className="md:hidden bg-bg border-t border-[#E5E1D8] overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} className="font-display text-2xl text-ink" end={n.to === "/"}>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/heritage-hotel#booking" className="btn-pill btn-primary mt-2 self-start" data-testid="mobile-book-btn">Reserve</Link>
              <div className="pt-4 border-t border-[#E5E1D8] text-sm text-ink-soft flex flex-col gap-2">
                <span className="flex items-center gap-2"><Phone size={14}/> {CONTACT.phone} · {CONTACT.phone2}</span>
                <span className="flex items-center gap-2"><Mail size={14}/> {CONTACT.email}</span>
                <span className="flex items-center gap-2"><MapPin size={14}/> Bundi, Rajasthan</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
